import styled from '@emotion/styled';
import HelpIcon from '@mui/icons-material/Help';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import routes from '../../config/routes';
import { isModeModalSelector, openSidebar, startGuidedMode, toggleSidebar } from '../../state';
import MenuHeader from './MenuHeader';
import Favicon from '../../img/favicon.png';

const AppBarStyled = styled(AppBar)`
  z-index: ${({ theme }) => theme.zIndex.drawer + 1};
  background: linear-gradient(
      120deg,
      ${({ theme }) => theme.palette.primary.dark},
      ${({ theme }) => theme.palette.primary.main} 68%
    ),
    ${({ theme }) => theme.palette.primary.main};
  border-bottom: 1px solid ${({ theme }) => alpha(theme.palette.common.white, 0.16)};
  box-shadow: ${({ theme }) => `0 14px 36px ${alpha(theme.palette.primary.dark, 0.24)}`};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(
      circle at 82% 0%,
      ${({ theme }) => alpha(theme.palette.secondary.main, 0.32)},
      transparent 24rem
    );
  }
`;

const TypographyStyled = styled(Typography)`
  flex-grow: 1;
  margin-left: ${({ theme }) => theme.spacing(1)};
  font-weight: ${({ theme }) => theme.typography.subtitle2.fontWeight};
  letter-spacing: 0.2px;
`;

const IconButtonRight = styled(IconButton)`
  margin-right: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => alpha(theme.palette.common.white, 0.12)};

  &:hover {
    background: ${({ theme }) => alpha(theme.palette.common.white, 0.22)};
  }
`;

const BackButtons = () => {
  const isModeModal = useSelector(isModeModalSelector);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(routes.protectedNestedRoutes.dashboard.index.path);
  };

  return isModeModal ? (
    <IconButtonRight
      color="inherit"
      aria-label={t('layout.header.aria.back')}
      edge="start"
      onClick={handleClick}
    >
      <ArrowBackIcon />
    </IconButtonRight>
  ) : null;
};

const MenuButton = () => {
  const isModeModal = useSelector(isModeModalSelector);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const handleClick = () => dispatch(toggleSidebar());
  return !isModeModal ? (
    <IconButtonRight
      color="inherit"
      aria-label={t('layout.header.aria.menu')}
      edge="start"
      onClick={handleClick}
    >
      <MenuIcon />
    </IconButtonRight>
  ) : null;
};

const Logo = () => {
  const isModeModal = useSelector(isModeModalSelector);
  const { t } = useTranslation();
  return (
    <Box
      component="img"
      sx={
        !isModeModal
          ? {
              height: 38,
              width: 38,
              borderRadius: 2,
              backgroundColor: theme => alpha(theme.palette.common.white, 0.16),
              p: 0.45,
            }
          : {
              height: 50,
              width: 50,
              borderRadius: 2,
              backgroundColor: theme => alpha(theme.palette.common.white, 0.16),
              p: 0.45,
            }
      }
      alt={t('shared.title')}
      src={Favicon}
    />
  );
};

const Header = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const handleStartGuidedMode = () => {
    dispatch(openSidebar());
    dispatch(startGuidedMode());
  };

  return (
    <AppBarStyled position="fixed" enableColorOnDark data-tour="app-header">
      <Toolbar sx={{ position: 'relative', zIndex: 1 }}>
        <MenuButton />
        <BackButtons />
        <Logo />
        <TypographyStyled variant="h6" noWrap>
          {t('shared.title')}
        </TypographyStyled>

        <Tooltip title={t('guidedMode.actions.start')}>
          <IconButton
            aria-label={t('layout.header.aria.guidedTour')}
            color="inherit"
            data-tour="guided-tour-button"
            onClick={handleStartGuidedMode}
            sx={theme => ({ backgroundColor: alpha(theme.palette.common.white, 0.1), ml: 1 })}
          >
            <MenuBookIcon />
          </IconButton>
        </Tooltip>
        <IconButton
          aria-label={t('layout.header.aria.help')}
          aria-controls="header-help"
          aria-haspopup="false"
          component={Link}
          to={routes.protectedRoutes.help.path}
          color="inherit"
          sx={theme => ({ backgroundColor: alpha(theme.palette.common.white, 0.1), ml: 1 })}
        >
          <HelpIcon />
        </IconButton>
        <MenuHeader />
      </Toolbar>
    </AppBarStyled>
  );
};

export default Header;
